import React, { useState } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Select,
  DatePicker,
  Checkbox,
  Typography,
  Divider,
} from "antd";
import {
  DownloadOutlined,
  CalendarOutlined,
  CaretDownOutlined,
} from "@ant-design/icons";
import "./Reports.css";

const { Title } = Typography;
const { Option } = Select;

const Reports: React.FC = () => {
  const [reportType, setReportType] = useState("Bike Shop Reimbursements");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [checkedFields, setCheckedFields] = useState<string[]>([]);

  const reportFields = [
    "Select All",
    "Bike Shop Name",
    "Bike Shop City",
    "Customer Incentive Code",
    "E-Bike Type",
    "Bike Shop POC First Name",
    "Bike Shop State",
    "Date of Sale",
    "Safety Gear Item",
    "Bike Shop POC Last Name",
    "Bike Shop Zip",
    "Date Approved/Declined",
    "Sale Price of E-Bike",
    "Bike Shop POC Phone Number",
    "Bike Shop Preferred Payment Type",
    "Approved/Declined By",
    "Reimbursement Amount",
    "Bike Shop POC Email",
    "Date Submitted",
    "Date Funded",
    "Bike Shop Address 1",
    "Customer Full Name",
    "Date Shipped",
    "Bike Shop Address 2",
    "Status",
  ];

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setCheckedFields(reportFields.slice(1)); // Exclude "Select All"
    } else {
      setCheckedFields([]);
    }
  };

  const handleFieldChange = (field: string, checked: boolean) => {
    if (field === "Select All") {
      handleSelectAll(checked);
    } else {
      if (checked) {
        setCheckedFields([...checkedFields, field]);
      } else {
        setCheckedFields(checkedFields.filter((f) => f !== field));
      }
    }
  };

  const isAllSelected = checkedFields.length === reportFields.length - 1;

  return (
    <div className="reports-page">
      {/* Page Header */}
      <div className="reports-header">
        <img
          src="/images/reports-icon.svg"
          alt="Reports"
          className="reports-icon"
        />
        <Title level={2} className="reports-title">
          Reports
        </Title>
      </div>

      {/* Main Content Card */}
      <Card className="reports-card">
        <Row gutter={20}>
          {/* Left Panel - Filters */}
          <Col xs={24} md={6} className="filters-column">
            <div className="filters-section">
              {/* Report Type */}
              <div className="filter-group">
                <label className="filter-label">Report Type</label>
                <Select
                  value={reportType}
                  onChange={setReportType}
                  className="filter-select"
                  suffixIcon={<CaretDownOutlined />}
                >
                  <Option value="Bike Shop Reimbursements">
                    Bike Shop Reimbursements
                  </Option>
                  <Option value="Consumer Applications">
                    Consumer Applications
                  </Option>
                  <Option value="Participating Shops">
                    Participating Shops
                  </Option>
                </Select>
              </div>

              {/* Start Date */}
              <div className="filter-group">
                <label className="filter-label">Start Date</label>
                <DatePicker
                  value={startDate}
                  onChange={setStartDate}
                  placeholder="MM/DD/YYYY"
                  className="filter-date"
                  suffixIcon={<CalendarOutlined />}
                />
              </div>

              {/* End Date */}
              <div className="filter-group">
                <label className="filter-label">End Date</label>
                <DatePicker
                  value={endDate}
                  onChange={setEndDate}
                  placeholder="MM/DD/YYYY"
                  className="filter-date"
                  suffixIcon={<CalendarOutlined />}
                />
              </div>
            </div>

            <Divider className="filters-divider" type="vertical" />
          </Col>

          {/* Right Panel - Report Content */}
          <Col xs={24} md={18} className="content-column">
            <div className="content-section">
              {/* Report Header */}
              <div className="report-header">
                <Title level={3} className="report-title">
                  Bike Shop Reimbursements Report
                </Title>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  className="download-button"
                >
                  Download Report
                </Button>
              </div>

              <Divider className="content-divider" />

              {/* Select All Checkbox */}
              <div className="select-all-section">
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={checkedFields.length > 0 && !isAllSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="select-all-checkbox"
                >
                  Select All
                </Checkbox>
              </div>

              {/* Field Selection Grid */}
              <div className="fields-grid">
                {reportFields.slice(1).map((field) => (
                  <div key={field} className="field-item">
                    <Checkbox
                      checked={checkedFields.includes(field)}
                      onChange={(e) =>
                        handleFieldChange(field, e.target.checked)
                      }
                      className="field-checkbox"
                    >
                      {field}
                    </Checkbox>
                  </div>
                ))}
              </div>
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default Reports;
